import React, { useRef } from "react";

import type {
  ExistingDepartmentReportMediaPatch,
  PendingDepartmentReportUpload,
  RequestDepartmentReportSavePayload,
} from "@core/reports/api/requestDepartmentReportSave";
import {
  requestDepartmentReportSaveAsync,
  type DepartmentReportScope,
} from "@core/reports/api/requestDepartmentReportSave";
import type { DepartmentReportMediaItem } from "@core/reports/api/types";
import {
  buildDepartmentReportMediaArgs,
  removeDepartmentReportMediaAsync,
  viewDepartmentReportMediaInNewTab,
} from "@core/reports/api/departmentReportMedia";
import { validateAttachment } from "@core/reports/constants/mediaAttachments";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";

export type UploadDraftRow = {
  file: File;
  caption: string;
};

export type UploadDraft = {
  categoryLabel: string;
  department_report_document_id: number | null;
  rows: UploadDraftRow[];
};

type UseDepartmentReportMediaArgs = {
  scope: DepartmentReportScope;
  entityId: number;
  departmentId: number;
  reportTagId: number;
  notes: string | null;
  departmentReportId: number | null;
  existingMedia: DepartmentReportMediaItem[];
  /** When true, upload + delete + caption edit are permitted (report is DRAFT / NEEDS_ATTENTION). */
  editable: boolean;
  /** Called after a successful media mutate so the page can refresh the report + media list. */
  onChanged: () => void | Promise<void>;
};

function originalCaption(media: DepartmentReportMediaItem): string {
  return media.description ?? "";
}

function scopeIdPart(scope: DepartmentReportScope, entityId: number) {
  switch (scope) {
    case "church":
      return { scope, churchId: entityId } as const;
    case "district":
      return { scope, districtId: entityId } as const;
    default:
      return { scope, nationId: entityId } as const;
  }
}

/**
 * Owns the per-entity ("own report") media state + handlers shared by the church,
 * district and nation report pages. File picks open a confirmation draft; Save
 * attaches immediately via report/save. Caption edits on existing files can be
 * saved inline the same way (and flushed on report save/submit).
 */
export function useDepartmentReportMedia({
  scope,
  entityId,
  departmentId,
  reportTagId,
  notes,
  departmentReportId,
  existingMedia,
  editable,
  onChanged,
}: UseDepartmentReportMediaArgs) {
  const otherFileInputRef = useRef<HTMLInputElement>(null);
  const namedFileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [uploadDraft, setUploadDraft] = React.useState<UploadDraft | null>(null);
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [captionDrafts, setCaptionDrafts] = React.useState<Record<number, string>>({});
  const [captionSavingId, setCaptionSavingId] = React.useState<number | null>(null);
  const [mediaActionId, setMediaActionId] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  /** Set when the next media list should replace every caption draft: a different report, or after a save. */
  const syncCaptionsRef = useRef(true);

  React.useEffect(() => {
    syncCaptionsRef.current = true;
    setCaptionDrafts({});
    setError(null);
  }, [scope, entityId, departmentId, reportTagId]);

  React.useEffect(() => {
    if (syncCaptionsRef.current) {
      syncCaptionsRef.current = false;
      setCaptionDrafts(
        Object.fromEntries(existingMedia.map((media) => [media.media_storage_id, originalCaption(media)]))
      );
      return;
    }
    setCaptionDrafts((current) => {
      let changed = false;
      const next = { ...current };
      for (const media of existingMedia) {
        if (next[media.media_storage_id] === undefined) {
          next[media.media_storage_id] = originalCaption(media);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [existingMedia]);

  /** Call before reloading after a report save so the drafts show what the server stored. */
  const resyncCaptionsFromServer = React.useCallback(() => {
    syncCaptionsRef.current = true;
  }, []);

  const acceptFile = (file: File): File | null => {
    const message = validateAttachment(file);
    if (message) {
      useErrorModalStore.getState().showValidation([message]);
      return null;
    }
    return file;
  };

  const openNamedUpload = (requirementId: number, categoryLabel: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !editable) return;
    const accepted = acceptFile(file);
    if (!accepted) return;
    setUploadError(null);
    setUploadDraft({
      categoryLabel,
      department_report_document_id: requirementId,
      rows: [{ file: accepted, caption: "" }],
    });
  };

  const openOtherUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0 || !editable) return;
    const accepted = selectedFiles.map(acceptFile).filter((file): file is File => file !== null);
    if (accepted.length === 0) return;
    setUploadError(null);
    setUploadDraft({
      categoryLabel: "Other",
      department_report_document_id: null,
      rows: accepted.map((file) => ({ file, caption: "" })),
    });
  };

  const setUploadDraftCaption = (index: number, caption: string) => {
    setUploadDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        rows: current.rows.map((row, i) => (i === index ? { ...row, caption } : row)),
      };
    });
  };

  const clearUploadDraft = () => {
    setUploadDraft(null);
    setUploadError(null);
    setUploadBusy(false);
    if (otherFileInputRef.current) otherFileInputRef.current.value = "";
  };

  const buildSavePayload = (
    attachments?: PendingDepartmentReportUpload[],
    existingMediaPatches?: ExistingDepartmentReportMediaPatch[]
  ): RequestDepartmentReportSavePayload => ({
    ...scopeIdPart(scope, entityId),
    departmentId,
    reportTagId,
    notes,
    attachments,
    existingMedia: existingMediaPatches,
  });

  const confirmUpload = async () => {
    if (!uploadDraft || uploadDraft.rows.length === 0 || uploadBusy) return;
    setUploadBusy(true);
    setUploadError(null);
    setError(null);
    try {
      const attachments: PendingDepartmentReportUpload[] = uploadDraft.rows.map((row) => ({
        file: row.file,
        department_report_document_id: uploadDraft.department_report_document_id,
        description: row.caption.trim() || null,
      }));
      const result = await requestDepartmentReportSaveAsync(buildSavePayload(attachments));
      if (!result.isSuccess()) {
        throw new Error(result.getError()?.detail || "Failed to attach file.");
      }
      clearUploadDraft();
      await onChanged();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Failed to attach file.");
    } finally {
      setUploadBusy(false);
    }
  };

  const setCaptionDraft = (mediaStorageId: number, description: string) => {
    setCaptionDrafts((current) => ({ ...current, [mediaStorageId]: description }));
  };

  const captionPatches = React.useMemo<ExistingDepartmentReportMediaPatch[]>(() => {
    return existingMedia
      .filter((media) => (captionDrafts[media.media_storage_id] ?? originalCaption(media)) !== originalCaption(media))
      .map((media) => ({
        media_storage_id: media.media_storage_id,
        description: captionDrafts[media.media_storage_id] ?? "",
      }));
  }, [existingMedia, captionDrafts]);

  const saveCaption = async (mediaStorageId: number) => {
    if (!editable || captionSavingId !== null || uploadBusy) return;
    const description = captionDrafts[mediaStorageId] ?? "";
    const media = existingMedia.find((item) => item.media_storage_id === mediaStorageId);
    if (!media || description === originalCaption(media)) return;

    setCaptionSavingId(mediaStorageId);
    setError(null);
    try {
      const result = await requestDepartmentReportSaveAsync(
        buildSavePayload(undefined, [{ media_storage_id: mediaStorageId, description }])
      );
      if (!result.isSuccess()) {
        throw new Error(result.getError()?.detail || "Failed to save caption.");
      }
      await onChanged();
      setCaptionDrafts((current) => ({ ...current, [mediaStorageId]: description }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save caption.");
    } finally {
      setCaptionSavingId(null);
    }
  };

  const viewMedia = async (media: DepartmentReportMediaItem) => {
    if (!departmentReportId || !media.media_storage_id) return;
    setMediaActionId(media.media_storage_id);
    setError(null);
    const message = await viewDepartmentReportMediaInNewTab(
      buildDepartmentReportMediaArgs(
        scope,
        entityId,
        departmentId,
        Number(departmentReportId),
        media.media_storage_id
      )
    );
    if (message) setError(message);
    setMediaActionId(null);
  };

  const handleRemoveMedia = async (media: DepartmentReportMediaItem) => {
    if (!departmentReportId || !media.media_storage_id || !editable) return;
    setMediaActionId(media.media_storage_id);
    setError(null);
    try {
      const result = await removeDepartmentReportMediaAsync(
        buildDepartmentReportMediaArgs(
          scope,
          entityId,
          departmentId,
          Number(departmentReportId),
          media.media_storage_id
        )
      );
      if (!result.isSuccess()) {
        throw new Error(result.getError()?.detail || "Failed to remove attachment.");
      }
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove attachment.");
    } finally {
      setMediaActionId(null);
    }
  };

  return {
    otherFileInputRef,
    namedFileInputRefs,
    uploadDraft,
    uploadBusy,
    uploadError,
    captionDrafts,
    captionPatches,
    captionSavingId,
    mediaActionId,
    error,
    openNamedUpload,
    openOtherUpload,
    setUploadDraftCaption,
    clearUploadDraft,
    confirmUpload,
    setCaptionDraft,
    resyncCaptionsFromServer,
    saveCaption,
    viewMedia,
    handleRemoveMedia,
  };
}
